import {
  Wheat,
  Layers,
  Package,
  Droplets,
  Sparkles,
  Trash2,
  Camera,
  ClipboardCheck,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import {
  Nav,
  Footer,
  PageHero,
  TrustStrip,
  RecyclingSubNav,
  usePageSeo,
  AGRI_IMG_URL,
  AGRI_SILAGE_URL,
  AGRI_SHEETING_URL,
  AGRI_BAGS_URL,
} from "../layout/shared";
import { SeparatedMaterials } from "../components/recycling/SeparatedMaterials";
import { PhotoEnquiry } from "../components/recycling/PhotoEnquiry";

const MATERIAL_GROUPS = [
  {
    heading: "Film and polythene",
    items: [
      "Silage and bale wrap",
      "Pit and clamp sheets",
      "Agricultural polythene",
      "Greenhouse and polytunnel film",
      "Clean stretch, shrink and packaging film",
    ],
    icon: Wheat,
    img: AGRI_SILAGE_URL,
  },
  {
    heading: "Sacks and flexible packaging",
    items: [
      "Fertiliser, seed and feed sacks",
      "Polypropylene woven bags",
      "Other pre-agreed agricultural packaging",
    ],
    icon: Package,
    img: AGRI_BAGS_URL,
  },
  {
    heading: "Other farm recyclables",
    items: [
      "Dry cardboard",
      "Selected rigid plastics",
      "Selected metals",
      "Expanded polystyrene where grade and volume are agreed",
    ],
    icon: Layers,
    img: AGRI_SHEETING_URL,
  },
];

const PREP_STEPS = [
  {
    icon: Sparkles,
    t: "Keep grades separate",
    d: "Keep film, woven sacks, rigid plastics, EPS, net wrap, twine and cardboard in separate material streams. Separate grades retain more recycling value and are quicker and safer to handle.",
  },
  {
    icon: Droplets,
    t: "Keep it clean and dry",
    d: "Shake out soil, stones, silage, feed and standing water. Store material under cover or in the agreed bags or containers where possible. Heavily contaminated loads may be rejected or reclassified.",
  },
  {
    icon: Trash2,
    t: "Remove non-plastic items",
    d: "Keep general waste and other non-recyclable material separate. Anything outside the agreed load needs separate assessment before booking.",
  },
  {
    icon: Camera,
    t: "Send photographs before collection",
    d: "Photograph each material pile, the loading position and the access. Include something that shows scale, then estimate the number of bags, bales, pallets or cubic metres.",
  },
];

const SEPARATE_APPROVAL = [
  "Batteries",
  "Electrical equipment",
  "Fluorescent tubes",
  "Tyres and tracks",
  "Contaminated and general farm waste",
];

const PROCESS = [
  {
    n: "1",
    t: "Send the details",
    d: "Postcode, farm name, contact number, photographs, material types, approximate quantity and access information.",
  },
  {
    n: "2",
    t: "We assess the load",
    d: "We confirm the grades, preparation requirements, collection method, documentation and price. Site visit if the photographs need supplementing.",
  },
  {
    n: "3",
    t: "Prepare and separate",
    d: "Keep each agreed grade separate and accessible. Do not add material after the load has been approved without checking with us.",
  },
  {
    n: "4",
    t: "Collection or booked delivery",
    d: "We confirm the date, vehicle, loading arrangements and who is responsible for loading.",
  },
  {
    n: "5",
    t: "Keep the record",
    d: "The relevant waste transfer and collection information is provided or retained for the agreed movement.",
  },
];

const FAQS = [
  {
    q: "Do farm plastics need to be washed?",
    a: "They should be shaken free of silage, soil, stones, feed and standing water and kept as dry as reasonably possible. The exact tolerance depends on the polymer and receiving outlet. Send photographs and we will confirm the preparation standard for that collection.",
  },
  {
    q: "Can all the plastic go in one bag?",
    a: "Usually not. Film, woven polypropylene sacks, rigid plastics, net wrap and twine are different material streams. Mixing them can reduce value or make a load unacceptable.",
  },
  {
    q: "Can you collect chemical drums and pesticide containers?",
    a: "Not as part of a standard farm-plastics collection. They require separate assessment, including their former contents, cleaning status, labels and the available authorised route.",
  },
  {
    q: "Do you provide paperwork?",
    a: "We provide or retain the waste transfer and collection records applicable to the movement. If you need a particular document for an assurance scheme, tell us before booking so we can confirm exactly what will be supplied.",
  },
  {
    q: "Do you cover small farms?",
    a: "Yes, subject to collection economics and access. A shared local round, a minimum collection quantity or a booked delivery may be the most cost-effective option for a smaller volume.",
  },
];

const Agricultural = () => {
  usePageSeo({
    title: "Farm Plastic Recycling · 3 Counties Recycling",
    description:
      "Farm plastic and packaging collections across Worcestershire, Herefordshire and Gloucestershire, with clear preparation guidance and documented routes.",
    path: "/agricultural-recycling",
  });

  return (
    <main data-testid="agricultural-root" className="bg-white text-[#1a1a1a]">
      <Nav />
      <RecyclingSubNav />
      <PageHero
        kicker="Farm plastics · packaging · rural collections"
        title="Farm plastic recycling across Worcestershire, Herefordshire and"
        accent="Gloucestershire."
        body="Clear the yard without losing the value in the material. We collect suitable farm plastics, sacks, packaging and other pre-agreed recyclables, with practical preparation guidance and documentation for the movement."
        image={AGRI_IMG_URL}
      />

      <section className="bg-white py-16 border-b border-[#024120]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <TrustStrip variant="light" />
        </div>
      </section>

      {/* Intro */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / Built around the way farms work
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
            The right collection depends on access, grade and how the material
            is stored.
          </h2>
          <p className="mt-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            Farm plastics are bulky, seasonal and often stored in awkward
            places. A collection only works properly when the right vehicle
            can reach the material, grades have been kept separate and the
            load is clean enough for the agreed recycling route. Tell us what
            has built up, how it is stored and where it can be loaded. We
            will confirm what can travel together, how it should be prepared
            and whether the best option is a collection, a shared local
            round or a booked delivery.
          </p>
        </div>
      </section>

      {/* Materials */}
      <section className="bg-[#F2F2F2] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
              / Materials we can assess
            </p>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
              Everything we handle is pre-agreed before it moves.
            </h2>
            <p className="mt-5 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
              Acceptance depends on the material, contamination, presentation,
              volume, site authorisation and current recycling outlet. Ask
              before mixing grades or loading a vehicle.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {MATERIAL_GROUPS.map((g) => (
              <article
                key={g.heading}
                data-testid={`materials-${g.heading
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-white border border-[#024120]/10 overflow-hidden flex flex-col"
              >
                <div className="relative h-44 bg-[#024120]">
                  <img
                    src={g.img}
                    alt={g.heading}
                    className="absolute inset-0 w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="p-7 flex-1 flex flex-col">
                  <span className="inline-flex h-11 w-11 items-center justify-center bg-[#024120] text-[#46B649] -mt-11 mb-4 shadow-md relative z-10">
                    <g.icon className="w-5 h-5" strokeWidth={1.6} />
                  </span>
                  <h3 className="font-display font-bold text-xl text-[#024120] tracking-tight">
                    {g.heading}
                  </h3>
                  <ul className="mt-4 space-y-2 text-[15px] text-[#4A4A4A] leading-relaxed">
                    {g.items.map((it) => (
                      <li key={it} className="flex gap-2">
                        <span className="text-[#46B649]">·</span> {it}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* How to prepare */}
      <section data-testid="recycling-before-booking" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
              / Before booking
            </p>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
              If in doubt, separate.
            </h2>
          </div>
          <SeparatedMaterials />
          <div className="grid md:grid-cols-2 gap-6">
            {PREP_STEPS.map((s) => (
              <div
                key={s.t}
                data-testid={`prep-${s.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-[#F2F2F2] p-7 border-l-4 border-[#46B649] flex items-start gap-5"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center bg-[#024120] text-[#46B649] shrink-0">
                  <s.icon className="w-6 h-6" strokeWidth={1.6} />
                </span>
                <div>
                  <p className="font-display font-bold text-lg text-[#024120] tracking-tight">
                    {s.t}
                  </p>
                  <p className="mt-2 text-[15px] text-[#4A4A4A] leading-relaxed">
                    {s.d}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documented route */}
      <section className="bg-[#024120] text-white py-24 md:py-32 grain overflow-hidden relative">
        <div className="max-w-4xl mx-auto px-6 md:px-10 relative">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#FF6600]">
            / Duty of care
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1]">
            A documented route for farm waste.
          </h2>
          <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed">
            Agricultural waste is business waste and remains subject to
            duty-of-care requirements. Collections are arranged through a
            registered waste carrier, and accepted material is transferred
            only to a site whose permit or registered exemption covers that
            waste and activity. Each movement is supported by the required
            waste transfer information. A season ticket may be appropriate
            for repeat transfers of the same non-hazardous waste, and
            non-hazardous transfer records must be retained for at least
            two years.
          </p>
          <p className="mt-4 text-white/80 text-base md:text-lg leading-relaxed">
            Non-packaging farm plastics may fall under 02 01 04; packaging
            usually falls under Chapter 15. The correct code depends on the
            material&rsquo;s origin, use and contamination and must be agreed
            before movement. We review the proposed documentation, but the
            producer remains responsible for accurate classification.
          </p>
        </div>
      </section>

      {/* Materials needing separate approval */}
      <section data-testid="recycling-do-not-add" className="bg-white py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="flex items-start gap-4 mb-8">
            <span className="inline-flex h-12 w-12 items-center justify-center bg-[#FF6600] text-white shrink-0">
              <AlertTriangle className="w-6 h-6" strokeWidth={1.7} />
            </span>
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
                / Materials needing separate approval
              </p>
              <h2 className="mt-2 font-display font-black text-2xl md:text-3xl tracking-tight text-[#024120] leading-[1.15]">
                Do not add the following to a standard farm-plastics load (unless pre-booked).
              </h2>
            </div>
          </div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {SEPARATE_APPROVAL.map((m, i) => (
              <li
                key={m}
                data-testid={`recycling-restricted-material-${i + 1}`}
                className="bg-[#F2F2F2] border-l-4 border-[#FF6600] px-5 py-3 text-[15px] text-[#024120]"
              >
                {m}
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-[#4A4A4A] leading-relaxed">
            Some of these materials may have a separate specialist route.
            Send details first and wait for written confirmation.
          </p>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#F2F2F2] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
              / Our collection process
            </p>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
              Five steps from full yard to a documented load.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map((s) => (
              <div
                key={s.n}
                data-testid={`process-step-${s.n}`}
                className="bg-white border border-[#024120]/10 p-6 flex flex-col"
              >
                <span className="font-display font-black text-4xl text-[#FF6600] leading-none">
                  {s.n}
                </span>
                <p className="mt-4 font-display font-bold text-lg text-[#024120]">
                  {s.t}
                </p>
                <p className="mt-2 text-[14px] text-[#4A4A4A] leading-relaxed">
                  {s.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Value chain */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10 text-center">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / From agriculture back into industry
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
            What leaves a farm should not automatically lose its value.
          </h2>
          <p className="mt-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
            When farm plastics and packaging are correctly separated, they
            can be baled or otherwise prepared and sent as segregated waste
            to an appropriately authorised reprocessor, where they may be
            converted into secondary raw material.
          </p>
          <div className="mt-12 grid md:grid-cols-4 gap-4 text-left">
            {[
              { t: "Farm", d: "Materials separated and stored." },
              {
                t: "3 Counties Recycling",
                d: "Collected, checked, sorted and prepared under the agreed route.",
              },
              {
                t: "Reprocessor",
                d: "Material cleaned and converted into a usable secondary feedstock.",
              },
              {
                t: "Manufacturer",
                d: "New products made where technically feasible and commercially viable.",
              },
            ].map((step, i) => (
              <div
                key={step.t}
                className="border border-[#024120]/10 p-5 relative"
              >
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#46B649]">
                  Step 0{i + 1}
                </p>
                <p className="mt-2 font-display font-bold text-[#024120]">
                  {step.t}
                </p>
                <p className="mt-2 text-[13px] text-[#4A4A4A] leading-relaxed">
                  {step.d}
                </p>
                {i < 3 && (
                  <ArrowRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#FF6600]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-[#F2F2F2] py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / Frequently asked questions
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
            Questions about your farm collection.
          </h2>

          <div className="mt-10 space-y-4">
            {FAQS.map((f, i) => (
              <details
                key={f.q}
                data-testid={`faq-${i + 1}`}
                className="bg-white border border-[#024120]/10 group"
              >
                <summary data-testid={`farm-faq-toggle-${i + 1}`} className="cursor-pointer list-none px-6 py-5 flex items-start justify-between gap-4 font-display font-bold text-[#024120] text-lg">
                  <span>{f.q}</span>
                  <ClipboardCheck className="w-5 h-5 text-[#46B649] shrink-0 mt-0.5" />
                </summary>
                <div className="px-6 pb-6 text-[15px] text-[#4A4A4A] leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <PhotoEnquiry
        id="farm"
        title="Send photos of your farm plastics."
        intro="Send photographs, the farm postcode and an estimate of the quantity. We will tell you how to prepare it and quote the most practical route."
        details={["Clear photographs of each separate material stream", "Your farm name and postcode", "The approximate quantity", "How the material is stored", "Vehicle access and loading information"]}
        message={"Hi 3 Counties Recycling, I would like a farm plastics collection assessment.\nFarm name: \nPostcode: \nMaterial types: \nApproximate quantity: \nStorage: \nVehicle access: \nI will attach photographs of each separate material stream."}
        quoteTo="/get-a-quote?service=agricultural"
      />

      <p className="max-w-4xl mx-auto px-6 md:px-10 py-8 text-xs text-[#4A4A4A] leading-relaxed italic border-t border-[#024120]/10">
        Materials are accepted only after assessment and written confirmation.
        The producer remains responsible for an accurate description and
        appropriate segregation. A collection booking does not authorise
        additional or undeclared material.
      </p>

      <Footer />

    </main>
  );
};

export default Agricultural;
